import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { articles, categories, tickerHeadlines } from "@/mocks/news";

const navLinks = [
  { label: "首页", to: "/" },
  ...categories.slice(0, 4).map((item) => ({
    label: item.name,
    to: `/category/${item.slug}`,
  })),
  { label: "关于我们", to: "/about" },
];

function formatToday() {
  const now = new Date();
  const week = ["日", "一", "二", "三", "四", "五", "六"][now.getDay()];
  return `${now.getFullYear()} 年 ${now.getMonth() + 1} 月 ${now.getDate()} 日 星期${week}`;
}

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
    setKeyword("");
  }, [location.pathname]);

  useEffect(() => {
    if (searchOpen) {
      searchRef.current?.focus();
    }
  }, [searchOpen]);

  const results = useMemo(() => {
    const key = keyword.trim().toLowerCase();
    if (!key) return [];
    return articles
      .filter(
        (item) =>
          item.title.toLowerCase().includes(key) ||
          item.summary.toLowerCase().includes(key) ||
          item.tags.some((tag) => tag.toLowerCase().includes(key)),
      )
      .slice(0, 6);
  }, [keyword]);

  const isActive = (to: string) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div className="hidden border-b border-background-200 bg-foreground-950 text-background-100 md:block">
        <div className="mx-auto flex h-9 max-w-site items-center gap-4 px-4 md:px-6">
          <span className="flex items-center gap-1.5 text-xs text-background-200">
            <i className="ri-calendar-line" aria-hidden="true"></i>
            <time dateTime={new Date().toISOString().slice(0, 10)}>{formatToday()}</time>
          </span>
          <span className="flex items-center gap-1.5 text-xs font-medium text-accent-300">
            <span className="h-1.5 w-1.5 rounded-full bg-accent-400"></span>
            实时更新中
          </span>
          <div className="relative flex-1 overflow-hidden">
            <div className="guwa-ticker flex w-max items-center gap-8 whitespace-nowrap">
              {[...tickerHeadlines, ...tickerHeadlines].map((line, index) => (
                <span key={`ticker-${index}`} className="text-xs text-background-200">
                  {line}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto flex h-16 max-w-site items-center justify-between gap-4 px-4 md:px-6">
        <Link to="/" className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
            <i className="ri-water-flash-line text-lg" aria-hidden="true"></i>
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-xl font-black tracking-tight text-foreground-950">
              看<span className="text-primary-600">瓜</span>爆料
            </span>
            <span className="mt-1 text-[10px] tracking-[0.2em] text-foreground-500">
              KANGUA BAOLIAO
            </span>
          </span>
        </Link>

        <nav aria-label="主导航" className="hidden items-center gap-1 lg:flex">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors ${
                isActive(link.to)
                  ? "bg-primary-100 font-medium text-primary-700"
                  : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setSearchOpen((prev) => !prev)}
            aria-label="搜索爆料"
            aria-expanded={searchOpen}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-200 text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
          >
            <i className={searchOpen ? "ri-close-line text-lg" : "ri-search-line text-lg"} aria-hidden="true"></i>
          </button>
          <Link
            to="/#today-feed"
            className="hidden items-center gap-1.5 whitespace-nowrap rounded-md bg-foreground-950 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 sm:flex"
          >
            <i className="ri-flashlight-line" aria-hidden="true"></i>
            今日速递
          </Link>
          <button
            type="button"
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-label="打开菜单"
            aria-expanded={menuOpen}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-200 text-foreground-800 lg:hidden"
          >
            <i className={menuOpen ? "ri-close-line text-lg" : "ri-menu-line text-lg"} aria-hidden="true"></i>
          </button>
        </div>
      </div>

      {searchOpen && (
        <div className="border-t border-background-200 bg-background-100">
          <div className="mx-auto max-w-site px-4 py-4 md:px-6">
            <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2">
              <i className="ri-search-line text-base text-foreground-500" aria-hidden="true"></i>
              <input
                ref={searchRef}
                type="search"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索瓜料、艺人或话题关键词"
                className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-500"
              />
            </div>
            {keyword.trim() && (
              <ul className="mt-3 space-y-1">
                {results.length === 0 && (
                  <li className="px-2 py-3 text-sm text-foreground-600">
                    暂时没有匹配的瓜料，换个关键词试试。
                  </li>
                )}
                {results.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => {
                        navigate(`/article/${item.id}`);
                        setSearchOpen(false);
                      }}
                      className="flex w-full cursor-pointer items-center justify-between gap-3 rounded-md px-2 py-2 text-left transition-colors hover:bg-background-200"
                    >
                      <span className="line-clamp-1 text-sm text-foreground-800">{item.title}</span>
                      <span className="whitespace-nowrap text-xs text-primary-600">{item.category}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {menuOpen && (
        <nav aria-label="移动端导航" className="border-t border-background-200 bg-background-100 lg:hidden">
          <ul className="mx-auto max-w-site px-4 py-3 md:px-6">
            {navLinks.map((link) => (
              <li key={link.to}>
                <Link
                  to={link.to}
                  className={`block rounded-md px-3 py-3 text-sm ${
                    isActive(link.to)
                      ? "bg-primary-100 font-medium text-primary-700"
                      : "text-foreground-700"
                  }`}
                >
                  {link.label}
                </Link>
              </li>
            ))}
            {categories.slice(4).map((item) => (
              <li key={item.slug}>
                <Link
                  to={`/category/${item.slug}`}
                  className="block rounded-md px-3 py-3 text-sm text-foreground-700"
                >
                  {item.name}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </header>
  );
}